<template>
  <v-form
    ref="formRef"
    class="free-renewable-form method-form"
    @submit.prevent=""
  >
    <!-- Anlagenstatus: only shown for reference -->
    <div v-if="reference" class="method-section">
      <div class="method-section-heading">
        <v-icon size="22" color="primary">
          mdi-check-circle-outline
        </v-icon>
        <span>Anlagenstatus</span>
      </div>

      <!-- Vorhanden -->
      <v-checkbox
        v-model="form.available"
        :error-messages="validationErrors?.available"
        label="Anlage vorhanden"
        class="mb-3"
      >
        <template #append>
          <v-tooltip v-if="hint.available" location="bottom" color="surface" content-class="tooltip">
            <template #activator="{ props }">
              <v-icon v-bind="props">
                mdi-information
              </v-icon>
            </template>

            <span>{{ hint.available }}</span>
          </v-tooltip>
        </template>
      </v-checkbox>

      <div class="section-description text-body-2 text-medium-emphasis">
        Aktivieren Sie diese Option, wenn die Anlage bereits im Referenzzustand
        vorhanden ist.
      </div>
    </div>

    <!-- All remaining fields are shown for planned state or an available reference -->
    <template v-if="form.available || !reference">
      <!-- Basisdaten -->
      <div class="method-section">
        <div class="method-section-heading">
          <v-icon size="22" color="primary">
            mdi-counter
          </v-icon>
          <span>Basisdaten</span>
        </div>

        <!-- Anzahl -->
        <v-text-field
          v-model.number="form.amount"
          type="number"
          :error-messages="validationErrors?.amount"
          label="Anzahl"
          :hint="hint.amount"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip v-if="hint.amount" location="bottom" color="surface" content-class="tooltip">
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.amount }}</span>
            </v-tooltip>

            <v-btn
              v-if="!reference && referenceData?.amount"
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Anzahl übernehmen"
              @click="copyFromReference('amount')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>

      <!-- Eigenverbrauch -->
      <div class="method-section">
        <div class="method-section-heading">
          <v-icon size="22" color="primary">
            mdi-home-lightning-bolt-outline
          </v-icon>
          <span>Eigenverbrauch</span>
        </div>

        <!-- Stromeigenverbrauch -->
        <v-text-field
          v-model.number="form.electricity_saving"
          type="number"
          :error-messages="validationErrors?.electricity_saving"
          label="Stromeigenverbrauch"
          suffix="kWh/a"
          :hint="hint.electricity_saving"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip
              v-if="hint.electricity_saving"
              location="bottom"
              color="surface" 
              content-class="tooltip"
            >
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.electricity_saving }}</span>
            </v-tooltip>

            <v-btn
              v-if="
                !reference &&
                referenceData?.electricity_saving
              "
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Stromeigenverbrauch übernehmen"
              @click="copyFromReference('electricity_saving')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <!-- Wärmeeigenverbrauch -->
        <v-text-field
          v-model.number="form.heat_saving"
          type="number"
          :error-messages="validationErrors?.heat_saving"
          label="Wärmeeigenverbrauch"
          suffix="kWh/a"
          :hint="hint.heat_saving"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip
              v-if="hint.heat_saving"
              location="bottom"
              color="surface" 
              content-class="tooltip"
            >
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.heat_saving }}</span>
            </v-tooltip>

            <v-btn
              v-if="
                !reference &&
                referenceData?.heat_saving
              "
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Wärmeeigenverbrauch übernehmen"
              @click="copyFromReference('heat_saving')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <!-- Kälteeigenverbrauch -->
        <v-text-field
          v-model.number="form.cold_saving"
          type="number"
          :error-messages="validationErrors?.cold_saving"
          label="Kälteeigenverbrauch"
          suffix="kWh/a"
          :hint="hint.cold_saving"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip
              v-if="hint.cold_saving"
              location="bottom"
              color="surface" 
              content-class="tooltip"
            >
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.cold_saving }}</span>
            </v-tooltip>

            <v-btn
              v-if="
                !reference &&
                referenceData?.cold_saving
              "
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Kälteeigenverbrauch übernehmen"
              @click="copyFromReference('cold_saving')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>

      <!-- Einspeisung -->
      <div class="method-section">
        <div class="method-section-heading">
          <v-icon size="22" color="primary">
            mdi-transmission-tower-export
          </v-icon>
          <span>Einspeisung</span>
        </div>

        <!-- Stromeinspeisung -->
        <v-text-field
          v-model.number="form.electricity_sold_amount"
          type="number"
          :error-messages="validationErrors?.electricity_sold_amount"
          label="Stromeinspeisung"
          suffix="kWh/a"
          :disabled="!form.available && reference"
          :hint="hint.electricity_sold_amount"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip
              v-if="hint.electricity_sold_amount"
              location="bottom"
              color="surface" 
              content-class="tooltip"
            >
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.electricity_sold_amount }}</span>
            </v-tooltip>

            <v-btn
              v-if="
                !reference &&
                referenceData?.electricity_sold_amount
              "
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Stromeinspeisung übernehmen"
              @click="copyFromReference('electricity_sold_amount')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <!-- Wärmeeinspeisung -->
        <v-text-field
          v-model.number="form.heat_sold_amount"
          type="number"
          :error-messages="validationErrors?.heat_sold_amount"
          label="Wärmeeinspeisung"
          suffix="kWh/a"
          :disabled="!form.available && reference"
          :hint="hint.heat_sold_amount"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip
              v-if="hint.heat_sold_amount"
              location="bottom"
              color="surface" 
              content-class="tooltip"
            >
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.heat_sold_amount }}</span>
            </v-tooltip>

            <v-btn
              v-if="
                !reference &&
                referenceData?.heat_sold_amount
              "
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Wärmeeinspeisung übernehmen"
              @click="copyFromReference('heat_sold_amount')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>

        <!-- Kälteeinspeisung -->
        <v-text-field
          v-model.number="form.cold_sold_amount"
          type="number"
          :error-messages="validationErrors?.cold_sold_amount"
          label="Kälteeinspeisung"
          suffix="kWh/a"
          :disabled="!form.available && reference"
          :hint="hint.cold_sold_amount"
          validate-on-blur
          class="mb-3"
        >
          <template #append>
            <v-tooltip
              v-if="hint.cold_sold_amount"
              location="bottom"
              color="surface" 
              content-class="tooltip"
            >
              <template #activator="{ props }">
                <v-icon v-bind="props">
                  mdi-information
                </v-icon>
              </template>

              <span>{{ hint.cold_sold_amount }}</span>
            </v-tooltip>

            <v-btn
              v-if="
                !reference &&
                referenceData?.cold_sold_amount
              "
              icon
              variant="text"
              title="Referenzwert übernehmen"
              aria-label="Referenzwert für Kälteeinspeisung übernehmen"
              @click="copyFromReference('cold_sold_amount')"
            >
              <v-icon>mdi-content-copy</v-icon>
            </v-btn>
          </template>
        </v-text-field>
      </div>
    </template>

    <!-- Reference empty state -->
    <div
      v-else-if="reference"
      class="renewable-empty-state"
    >
      <v-icon
        size="44"
        color="primary"
        class="mb-3"
      >
        mdi-solar-power-variant-outline
      </v-icon>

      <div class="text-subtitle-1 font-weight-bold">
        Keine bestehende Anlage
      </div>

      <div class="text-body-2 text-medium-emphasis mt-1">
        Aktivieren Sie „Anlage vorhanden“, um Referenzwerte zu erfassen.
      </div>
    </div>
  </v-form>
</template>

<script setup>
import { ref, computed, watch, onMounted } from 'vue'
import { useBaseForm } from '@/composables/useBaseForm'

/* Props as required by parent (action.vue) */
const props = defineProps({
  modelValue: { type: Object, default: () => ({}) },
  reference:  { type: Boolean, default: false },
  planned:    { type: Boolean, default: false },
  otherData:  { type: Object, default: () => ({}) },
  sharedData: { type: Object, default: () => ({}) },
  validationErrors: { type: Object, default: () => ({}) },
  methodValue: { type: String, default: '' },
})
const emit = defineEmits(['update:modelValue'])

/* Frontend -> Backend field mapping */
const fields = {
  available:               'available',
  amount:                  'amount',
  electricity_saving:      'electricity_saving',
  electricity_sold_amount: 'electricity_sold_amount',
  heat_saving:             'heat_saving',
  heat_sold_amount:        'heat_sold_amount',
  cold_saving:             'cold_saving',
  cold_sold_amount:        'cold_sold_amount',
}

/* useBaseForm integration */
const {
  form,
  hint,
  referenceData,
  copyFromReference,
  fetchHints,
} = useBaseForm({
  value: computed(() => props.modelValue),
  fields,
  defaults: {
    available: false,
    electricity_sold_amount: 0,
    heat_sold_amount: 0,
    cold_sold_amount: 0,
  },
  reference:  computed(() => props.reference),
  planned:    computed(() => props.planned),
  shared:     computed(() => false), // not a shared section
  otherData:  computed(() => props.otherData),
  sharedData: computed(() => props.sharedData),
  methodValue: computed(() => props.methodValue),
  emit: (event, payload) => emit(event, payload),
})

/* Behavior parity: when 'available' becomes false on reference form, zero out totals */
watch(
  () => form.available,
  (val) => {
    if (!val && props.reference) {
      form.amount = 0
      form.electricity_saving = 0
      form.heat_saving = 0
      form.cold_saving = 0
      form.electricity_sold_amount = 0
      form.heat_sold_amount = 0
      form.cold_sold_amount = 0
    }
  }
)

/* Expose validateForm for parent error flow */
const formRef = ref(null)
function validateForm() {
  return formRef.value?.validate?.()
}

defineExpose({
  validateForm,
  fields: Object.values(fields),
})


/* Lifecycle */
onMounted(async () => {
  await fetchHints()
})
</script>

<style scoped lang="scss">
.free-renewable-form {
  width: 100%;
}

/* Input fields */
.free-renewable-form :deep(.v-field) {
  border-radius: 18px;
}

.free-renewable-form :deep(.v-input) {
  margin-bottom: 0.35rem;
}

/* Visual sections */
.method-section {
  margin-bottom: 1.25rem;
  padding: 1rem;
  border: 1px solid rgba(var(--v-theme-on-surface), 0.07);
  border-radius: 24px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

.method-section:last-child {
  margin-bottom: 0;
}

/* Section headings */
.method-section-heading {
  display: flex;
  align-items: center;
  gap: 0.55rem;

  margin-bottom: 1rem;
  padding: 0.75rem 0.9rem;

  border: 1px solid rgba(var(--v-theme-primary), 0.1);
  border-radius: 18px;

  color: rgb(var(--v-theme-primary));
  background-color: rgba(var(--v-theme-primary), 0.055);

  font-size: 1rem;
  font-weight: 700;
}

/* Checkbox */
.free-renewable-form :deep(.v-checkbox) {
  padding: 0.35rem 0.65rem;
  border-radius: 18px;
  background-color: rgba(var(--v-theme-primary), 0.025);
}

/* Section description */
.section-description {
  padding: 0 0.65rem 0.35rem;
  line-height: 1.45;
}

/* Help and reference-copy actions */
.free-renewable-form :deep(.v-input__append),
.free-renewable-form :deep(.v-field__append-inner) {
  align-items: center;
}

.free-renewable-form :deep(.v-input__append .v-icon),
.free-renewable-form :deep(.v-field__append-inner .v-icon) {
  color: rgb(var(--v-theme-primary));
  opacity: 0.85;
}

.free-renewable-form :deep(.v-input__append .v-btn),
.free-renewable-form :deep(.v-field__append-inner .v-btn) {
  border-radius: 999px;
  color: rgb(var(--v-theme-primary));
}

.free-renewable-form :deep(.v-input__append .v-btn:hover),
.free-renewable-form :deep(.v-field__append-inner .v-btn:hover) {
  background-color: rgba(var(--v-theme-primary), 0.08);
}

/* Empty reference state */
.renewable-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;

  padding: 2rem 1rem;
  border: 1px dashed rgba(var(--v-theme-primary), 0.24);
  border-radius: 24px;

  background-color: rgba(var(--v-theme-primary), 0.025);
  text-align: center;
}

/* Disabled states */
.free-renewable-form :deep(.v-field--disabled) {
  opacity: 0.72;
}

/* Responsive */
@media (max-width: 700px) {
  .method-section {
    padding: 0.8rem;
    border-radius: 20px;
  }

  .method-section-heading {
    align-items: flex-start;
    font-size: 0.95rem;
  }

  .renewable-empty-state {
    padding: 1.5rem 0.8rem;
    border-radius: 20px;
  }
}
</style>
